import React, { useCallback, useEffect, useState } from "react";
import { pedidoBackend, registrarEvento } from "../../registro/registro";
import { createClient } from "../../axios/axiosForm";
import { formatoPrecio } from "../../utils/formatPrecio";
import Modal from "../../components/modal/Modal";
import Spinner from "../../components/spinner/Spinner";
import {
  Aviso,
  Badge,
  Boton,
  Chip,
  Chips,
  Detalle,
  Filtros,
  Kpi,
  Kpis,
  Paginacion,
  Panel,
  Tabla,
  TablaWrapper,
  Vacio,
} from "./panelStyles";

const ESTADOS = [
  { valor: "", texto: "Todas" },
  { valor: "OK", texto: "Altas OK" },
  { valor: "ERROR", texto: "Rechazadas" },
  { valor: "INCIERTO", texto: "Sin confirmar" },
  { valor: "PENDIENTE", texto: "Sin resultado" },
];

const hoy = () => new Date().toISOString().slice(0, 10);
const primeroDelMes = () => `${new Date().toISOString().slice(0, 7)}-01`;

const fecha = (iso) =>
  iso
    ? new Date(iso).toLocaleString("es-AR", {
        timeZone: "America/Argentina/Buenos_Aires",
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

// Un dato del detalle: etiqueta arriba, valor abajo. Si no hay valor, no se
// muestra la tarjeta (así el modal no queda con campos vacíos).
const Campo = ({ k, v, ancho }) => {
  if (v == null || v === "") return null;
  return (
    <div className={ancho ? "campo ancho" : "campo"}>
      <div className="k">{k}</div>
      <div className="v">{v}</div>
    </div>
  );
};

const mensajeError = (e) =>
  e?.response?.data?.error || e?.message || "No se pudo consultar el panel";

const VentasPanel = ({ esIT = false }) => {
  const [filtros, setFiltros] = useState({
    desde: primeroDelMes(),
    hasta: hoy(),
    usuario: "",
    estado: "",
    asignar: false,
    q: "",
  });
  const [page, setPage] = useState(1);
  const [resumen, setResumen] = useState([]);
  const [operaciones, setOperaciones] = useState([]);
  const [paging, setPaging] = useState({ page: 1, totalPages: 1, total: 0 });
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [mensaje, setMensaje] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [ocupado, setOcupado] = useState(false);
  // Reintento de alta (solo IT, solo ventas en ERROR): el JSON de envío editable,
  // el paso de confirmación y el resultado del reenvío a Iptel.
  const [payloadEdit, setPayloadEdit] = useState("");
  const [jsonError, setJsonError] = useState(null);
  const [confirmandoReintento, setConfirmandoReintento] = useState(false);
  const [reintentando, setReintentando] = useState(false);
  const [resultadoReintento, setResultadoReintento] = useState(null);
  // Opciones del filtro "Vendedor": todos los que cargaron alguna venta, sin
  // depender de los filtros (si no, al elegir uno desaparecían los demás).
  const [vendedores, setVendedores] = useState([]);

  useEffect(() => {
    pedidoBackend("get", "/panel/vendedores")
      .then(({ data }) => setVendedores(data.data || []))
      .catch(() => setVendedores([]));
  }, []);

  const params = useCallback(() => {
    const p = { desde: filtros.desde, hasta: filtros.hasta };
    if (filtros.usuario) p.usuario = filtros.usuario;
    if (filtros.estado) p.estado = filtros.estado;
    if (filtros.asignar) p.asignar = "true";
    if (filtros.q.trim()) p.q = filtros.q.trim();
    return p;
  }, [filtros]);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const [r, o] = await Promise.all([
        pedidoBackend("get", "/panel/resumen", { params: params() }),
        pedidoBackend("get", "/panel/operaciones", { params: { ...params(), page, pageSize: 50 } }),
      ]);
      setResumen(r.data.data || []);
      setOperaciones(o.data.data || []);
      setPaging(o.data.paging || { page: 1, totalPages: 1, total: 0 });
    } catch (e) {
      setError(mensajeError(e));
      setResumen([]);
      setOperaciones([]);
    } finally {
      setCargando(false);
    }
  }, [params, page]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const cambiar = (campo, valor) => {
    setPage(1);
    setFiltros((f) => ({ ...f, [campo]: valor }));
  };

  const totales = resumen.reduce(
    (acc, f) => ({
      total: acc.total + f.total,
      ok: acc.ok + f.ok,
      error: acc.error + f.error,
      incierto: acc.incierto + f.incierto,
      aAsignar: acc.aAsignar + f.aAsignar,
    }),
    { total: 0, ok: 0, error: 0, incierto: 0, aAsignar: 0 },
  );

  const descargarExcel = async () => {
    setOcupado(true);
    setMensaje(null);
    try {
      const res = await pedidoBackend("get", "/panel/export.xlsx", {
        params: params(),
        responseType: "blob",
      });
      const nombre =
        /filename="([^"]+)"/.exec(res.headers["content-disposition"] || "")?.[1] ||
        "operaciones.xlsx";
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = nombre;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // El archivo tiene datos de clientes: no dejar el blob colgado en memoria.
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) {
      setMensaje({ texto: mensajeError(e), error: true });
    } finally {
      setOcupado(false);
    }
  };

  const enviarPorMail = async () => {
    setOcupado(true);
    setMensaje(null);
    try {
      const { data } = await pedidoBackend("post", "/panel/reportes", { datos: params() });
      setMensaje({
        texto: data.data.simulado
          ? `El reporte se generó (${data.data.operaciones} operaciones) pero el envío de mails no está configurado todavía.`
          : `Reporte enviado a ${data.data.enviadoA} (${data.data.operaciones} operaciones).`,
        error: Boolean(data.data.simulado),
      });
    } catch (e) {
      setMensaje({ texto: mensajeError(e), error: true });
    } finally {
      setOcupado(false);
    }
  };

  const verDetalle = async (id) => {
    try {
      const { data } = await pedidoBackend("get", `/panel/operaciones/${id}`);
      setDetalle(data.data);
    } catch (e) {
      setMensaje({ texto: mensajeError(e), error: true });
    }
  };

  // Al abrir (o cambiar de) venta en el modal, precargo el editor de JSON con lo
  // que se le envió a Iptel y reseteo el estado del reintento.
  useEffect(() => {
    if (detalle) {
      setPayloadEdit(JSON.stringify(detalle.payload ?? {}, null, 2));
    } else {
      setPayloadEdit("");
    }
    setJsonError(null);
    setConfirmandoReintento(false);
    setResultadoReintento(null);
  }, [detalle]);

  // Reintento del alta (solo IT, solo ERROR). Reenvía el JSON (editable) DIRECTO
  // a Iptel con la sesión del que está logueado —igual que el formulario—, no
  // pasa por nuestro backend. El alta se atribuye al `usuarioId` que viaja en el
  // cuerpo (el del vendedor original); la respuesta de Iptel trae `data.usuario`,
  // que mostramos para confirmar a nombre de quién quedó.
  const reintentar = async () => {
    let payloadObj;
    try {
      payloadObj = JSON.parse(payloadEdit);
    } catch (e) {
      setJsonError(`El JSON no es válido: ${e.message}`);
      return;
    }
    setJsonError(null);
    setReintentando(true);
    setResultadoReintento(null);
    const inicio = Date.now();
    try {
      const response = await createClient(payloadObj);
      const dur = Date.now() - inicio;
      const ok = response?.status?.success === true;
      const clienteId = ok ? response?.data?.id ?? null : null;
      const usuarioAtribuido = ok ? response?.data?.usuario ?? null : null;
      const estadoResultado = ok
        ? "OK"
        : response?.sinRespuesta
          ? "INCIERTO"
          : "ERROR";
      setResultadoReintento({
        ok,
        estadoResultado,
        clienteId,
        usuarioAtribuido,
        mensaje: ok
          ? response?.status?.message || "Alta registrada."
          : response?.message || "Iptel no confirmó el alta.",
      });
      // Traza en la bitácora (fire-and-forget, no afecta el reintento).
      registrarEvento(
        "venta.reintento",
        `Reintento del alta ${detalle.id} (vendedor ${detalle.usuario}): ${
          ok ? `OK cliente ${clienteId} atribuido a ${usuarioAtribuido}` : estadoResultado
        }`,
        {
          operacionOrigen: detalle.id,
          origenIdOriginal: detalle.origenId,
          usuarioOriginal: detalle.usuario,
          estadoResultado,
          clienteId,
          usuarioAtribuido,
          httpStatus: response?.httpStatus ?? null,
          duracionMs: dur,
          payloadEditado: payloadObj,
        },
      );
      if (ok) setConfirmandoReintento(false);
    } catch (e) {
      setResultadoReintento({
        ok: false,
        estadoResultado: "ERROR",
        mensaje: e?.message || "Error inesperado al reenviar.",
      });
    } finally {
      setReintentando(false);
    }
  };

  const reenviarAviso = async (id) => {
    setOcupado(true);
    setMensaje(null);
    try {
      const { data } = await pedidoBackend("post", `/panel/operaciones/${id}/reenviar-aviso`);
      setMensaje({
        texto: data.data.avisoEnviadoAt
          ? "Aviso reenviado."
          : `El aviso no salió: ${data.data.avisoError || "revisá el mail del vendedor y el SMTP"}.`,
        error: !data.data.avisoEnviadoAt,
      });
      cargar();
    } catch (e) {
      setMensaje({ texto: mensajeError(e), error: true });
    } finally {
      setOcupado(false);
    }
  };

  return (
    <>
      <Panel>
        <Filtros>
          <label>
            Desde
            <input type="date" value={filtros.desde} onChange={(e) => cambiar("desde", e.target.value)} />
          </label>
          <label>
            Hasta
            <input type="date" value={filtros.hasta} onChange={(e) => cambiar("hasta", e.target.value)} />
          </label>
          <label>
            Vendedor
            <select value={filtros.usuario} onChange={(e) => cambiar("usuario", e.target.value)}>
              <option value="">Todos</option>
              {vendedores.map((v) => (
                <option key={v.usuario} value={v.usuario}>
                  {v.nombre ? `${v.nombre} (${v.usuario})` : v.usuario}
                </option>
              ))}
            </select>
          </label>
          <label>
            Buscar
            <input
              type="search"
              placeholder="DNI, apellido o N° de cliente"
              value={filtros.q}
              onChange={(e) => cambiar("q", e.target.value)}
            />
          </label>
          <label style={{ flexDirection: "row", alignItems: "center", gap: "0.4rem" }}>
            <input
              type="checkbox"
              style={{ minWidth: "auto" }}
              checked={filtros.asignar}
              onChange={(e) => cambiar("asignar", e.target.checked)}
            />
            Solo instalaciones a asignar
          </label>
          <Boton type="button" onClick={descargarExcel} disabled={ocupado || cargando}>
            ⬇ Descargar Excel
          </Boton>
          <Boton type="button" $secundario onClick={enviarPorMail} disabled={ocupado || cargando}>
            ✉ Enviarme por mail
          </Boton>
        </Filtros>

        <Chips>
          {ESTADOS.map((e) => (
            <Chip
              key={e.valor || "todas"}
              type="button"
              $activo={filtros.estado === e.valor}
              onClick={() => cambiar("estado", e.valor)}
            >
              {e.texto}
            </Chip>
          ))}
        </Chips>

        {mensaje && <Aviso $error={mensaje.error}>{mensaje.texto}</Aviso>}
        {error && <Aviso $error>{error}</Aviso>}
      </Panel>

      <Kpis>
        <Kpi>
          <strong>{totales.total}</strong>
          <span>Ventas cargadas</span>
        </Kpi>
        <Kpi $color="#1e7d4f">
          <strong>{totales.ok}</strong>
          <span>Altas confirmadas</span>
        </Kpi>
        <Kpi $color="#c0392b">
          <strong>{totales.error}</strong>
          <span>Rechazadas</span>
        </Kpi>
        <Kpi $color="#b8860b">
          <strong>{totales.incierto}</strong>
          <span>Sin confirmar</span>
        </Kpi>
        <Kpi $color="var(--naranjaOscuro)">
          <strong>{totales.aAsignar}</strong>
          <span>Instalación a asignar</span>
        </Kpi>
      </Kpis>

      {resumen.length > 1 && (
        <TablaWrapper style={{ marginBottom: "1.25rem" }}>
          <Tabla $min="700px">
            <thead>
              <tr>
                <th>Vendedor</th>
                <th>Usuario</th>
                <th>Ventas</th>
                <th>Altas OK</th>
                <th>Rechazadas</th>
                <th>Sin confirmar</th>
                <th>A asignar</th>
                <th>Última</th>
              </tr>
            </thead>
            <tbody>
              {resumen.map((f) => (
                <tr key={f.usuario}>
                  <td>{f.promotorNombre || "—"}</td>
                  <td>{f.usuario}</td>
                  <td>{f.total}</td>
                  <td>{f.ok}</td>
                  <td>{f.error}</td>
                  <td>{f.incierto}</td>
                  <td>{f.aAsignar || "—"}</td>
                  <td>{fecha(f.ultima)}</td>
                </tr>
              ))}
            </tbody>
          </Tabla>
        </TablaWrapper>
      )}

      <TablaWrapper>
        <Tabla $min="1250px">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Vendedor</th>
              <th>Cliente</th>
              <th>DNI</th>
              <th>N° cliente</th>
              <th>Plan</th>
              <th>Total</th>
              <th>Estado</th>
              <th>Instalación</th>
              <th> </th>
            </tr>
          </thead>
          <tbody>
            {cargando && (
              <tr>
                <Vacio colSpan={10}>
                  <Spinner />
                </Vacio>
              </tr>
            )}
            {!cargando && operaciones.length === 0 && (
              <tr>
                <Vacio colSpan={10}>No hay ventas con estos filtros.</Vacio>
              </tr>
            )}
            {!cargando &&
              operaciones.map((o) => (
                <tr key={o.id}>
                  <td>{fecha(o.fechaVenta)}</td>
                  <td>
                    {o.promotorNombre || o.usuario}
                    <div style={{ color: "var(--grisMedio)", fontSize: "0.75rem" }}>{o.usuario}</div>
                  </td>
                  <td>
                    {[o.clienteApellido, o.clienteNombre].filter(Boolean).join(", ") || "—"}
                  </td>
                  <td>{o.clienteDni || "—"}</td>
                  <td>{o.clienteId || "—"}</td>
                  <td title={o.planNombre || ""}>
                    {o.planNombre ? o.planNombre.replace(/^.*? - /, "") : "—"}
                  </td>
                  <td>{o.total != null ? formatoPrecio(o.total) : "—"}</td>
                  <td>
                    <Badge $estado={o.estado}>{o.estado}</Badge>
                    {o.respuestaMensaje && o.estado !== "OK" && (
                      <div style={{ color: "var(--grisMedio)", fontSize: "0.75rem", maxWidth: 220 }}>
                        {o.respuestaMensaje}
                      </div>
                    )}
                  </td>
                  <td>
                    {o.asignarA ? (
                      <>
                        <strong>{o.asignarA}</strong>
                        <div style={{ fontSize: "0.75rem", color: o.avisoEnviadoAt ? "#1e7d4f" : "#c0392b" }}>
                          {o.avisoEnviadoAt ? `avisado ${fecha(o.avisoEnviadoAt)}` : "aviso pendiente"}
                        </div>
                      </>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <Boton type="button" $secundario onClick={() => verDetalle(o.id)}>
                      Ver
                    </Boton>{" "}
                    {o.asignarA && (o.estado === "OK" || o.estado === "INCIERTO") && (
                      <Boton type="button" $secundario disabled={ocupado} onClick={() => reenviarAviso(o.id)}>
                        Reenviar aviso
                      </Boton>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </Tabla>
        <Paginacion>
          <span>
            {paging.total} operaciones · página {paging.page} de {paging.totalPages}
          </span>
          <Boton type="button" $secundario disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            ← Anterior
          </Boton>
          <Boton
            type="button"
            $secundario
            disabled={page >= paging.totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Siguiente →
          </Boton>
        </Paginacion>
      </TablaWrapper>

      <Modal isOpen={Boolean(detalle)} onClose={() => setDetalle(null)} size="wide">
        {detalle && (
          <Detalle>
            <div className="cabecera">
              <h3>Venta de {detalle.promotorNombre || detalle.usuario}</h3>
              <Badge $estado={detalle.estado}>{detalle.estado}</Badge>
            </div>
            <p className="sub">
              {fecha(detalle.fechaVenta)} · usuario {detalle.usuario}
              {detalle.promotorId ? ` · promotor ${detalle.promotorId}` : ""}
            </p>

            <h4>Cliente</h4>
            <div className="grid">
              <Campo k="Apellido y nombre" v={[detalle.clienteApellido, detalle.clienteNombre].filter(Boolean).join(", ")} />
              <Campo k="DNI" v={detalle.clienteDni} />
              <Campo k="N° de cliente" v={detalle.clienteId || "sin confirmar"} />
              <Campo k="Teléfonos" v={(detalle.resumen?.telefonos || []).map((t) => t.numero).join(" · ")} />
              <Campo k="Email" v={detalle.resumen?.email} ancho />
            </div>

            <h4>Domicilio</h4>
            <div className="grid">
              <Campo
                ancho
                k="Dirección"
                v={
                  [
                    [detalle.resumen?.calle, detalle.resumen?.altura].filter(Boolean).join(" "),
                    detalle.resumen?.piso && `piso ${detalle.resumen.piso}`,
                    detalle.resumen?.dpto && `dpto ${detalle.resumen.dpto}`,
                    detalle.resumen?.localidad,
                  ]
                    .filter(Boolean)
                    .join(", ")
                }
              />
              <Campo k="Referencia" v={detalle.resumen?.ubicacion} ancho />
            </div>

            <h4>Plan e instalación</h4>
            <div className="grid">
              <Campo k="Plan" v={detalle.planNombre} ancho />
              <Campo k="Total 1ª factura" v={detalle.total != null ? formatoPrecio(detalle.total) : null} />
              <Campo
                k="Instalación"
                ancho
                v={
                  detalle.asignarA
                    ? `A asignar a ${detalle.asignarA}${
                        detalle.avisoEnviadoAt
                          ? ` · avisado ${fecha(detalle.avisoEnviadoAt)}`
                          : " · aviso pendiente"
                      }${detalle.avisoError ? ` (${detalle.avisoError})` : ""}`
                    : "Sin asignación especial"
                }
              />
            </div>

            <h4>Registro</h4>
            <div className="grid">
              <Campo k="Estado" v={detalle.respuestaMensaje || detalle.estado} ancho />
              <Campo k="IP" v={detalle.ip} />
              <Campo k="Demoró" v={detalle.duracionMs != null ? `${detalle.duracionMs} ms` : null} />
              <Campo k="Referencia interna" v={detalle.id} ancho />
            </div>

            <details>
              <summary>Datos técnicos (JSON)</summary>
              <h5>Respuesta de Iptel</h5>
              <pre>{detalle.respuesta ? JSON.stringify(detalle.respuesta, null, 2) : "—"}</pre>
              <h5>Datos enviados</h5>
              <pre>{JSON.stringify(detalle.payload, null, 2)}</pre>
            </details>

            {esIT && detalle.estado === "ERROR" && (
              <div
                style={{
                  marginTop: "1rem",
                  padding: "1rem",
                  border: "1px solid var(--grisClaro, #ddd)",
                  borderRadius: 8,
                  background: "var(--grisFondo, #faf9fc)",
                }}
              >
                <h4 style={{ marginTop: 0 }}>Reintentar envío (IT)</h4>
                <p style={{ fontSize: "0.82rem", color: "var(--grisMedio)", marginTop: 0 }}>
                  Reenvía este alta <strong>directo a Iptel</strong> con tu sesión. La venta se
                  atribuye al vendedor del cuerpo (<strong>{detalle.usuario}</strong>); al salir OK
                  se muestra a nombre de quién quedó según Iptel. Podés editar el JSON antes de
                  reenviar para corregir el campo que falló.
                </p>
                <textarea
                  value={payloadEdit}
                  onChange={(e) => setPayloadEdit(e.target.value)}
                  spellCheck={false}
                  rows={14}
                  style={{
                    width: "100%",
                    fontFamily: "monospace",
                    fontSize: "0.8rem",
                    padding: "0.6rem",
                    border: "1px solid var(--grisClaro, #ccc)",
                    borderRadius: 6,
                    resize: "vertical",
                    boxSizing: "border-box",
                  }}
                />
                {jsonError && (
                  <Aviso $error style={{ marginTop: "0.5rem" }}>
                    {jsonError}
                  </Aviso>
                )}

                {resultadoReintento && (
                  <Aviso $error={!resultadoReintento.ok} style={{ marginTop: "0.5rem" }}>
                    {resultadoReintento.ok ? (
                      <>
                        ✅ Alta OK — cliente <strong>{resultadoReintento.clienteId}</strong>.
                        Atribuida a: <strong>{resultadoReintento.usuarioAtribuido || "—"}</strong>
                      </>
                    ) : (
                      <>
                        {resultadoReintento.estadoResultado === "INCIERTO"
                          ? "⚠️ Sin confirmación de Iptel (el alta PUEDE haberse creado): "
                          : "❌ No se pudo: "}
                        {resultadoReintento.mensaje}
                      </>
                    )}
                  </Aviso>
                )}

                <div style={{ marginTop: "0.75rem", display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                  {!confirmandoReintento ? (
                    <Boton type="button" onClick={() => setConfirmandoReintento(true)}>
                      Reintentar envío
                    </Boton>
                  ) : (
                    <>
                      <Boton type="button" disabled={reintentando} onClick={reintentar}>
                        {reintentando ? "Enviando…" : "Confirmar reenvío a Iptel"}
                      </Boton>
                      <Boton
                        type="button"
                        $secundario
                        disabled={reintentando}
                        onClick={() => setConfirmandoReintento(false)}
                      >
                        Cancelar
                      </Boton>
                    </>
                  )}
                </div>
              </div>
            )}
          </Detalle>
        )}
      </Modal>
    </>
  );
};

export default VentasPanel;
